import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { IPermission, ITransferListItem } from "@miplatform/types";
import { TransferList } from "@mibuildweb/components";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IRoleFormData } from "../../ConfigRoles.types";
import { getPermissionNameType } from "../utils/getModulesByAccessPermissions";

export const RolePermissionsSubTabView = ({
  form,
  extraTabProps,
}: {
  form: UseFormReturn<IRoleFormData>;
  extraTabProps?: {
    availablePermissions?: IPermission[];
    selectedPermissions?: IPermission[];
    isReadOnly?: boolean;
    categoryName?: string;
  };
}) => {
  const { getValues, setValue } = form;
  const {
    isReadOnly,
    availablePermissions,
    selectedPermissions,
    categoryName,
  } = extraTabProps || {};

  const addPermission = (permissionName: string) => {
    const currentPermissions = getValues().permissionNames || [];
    setValue("permissionNames", [...currentPermissions, permissionName], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const removePermission = (permissionName: string) => {
    const currentPermissions = getValues().permissionNames || [];
    setValue(
      "permissionNames",
      currentPermissions.filter((name) => name !== permissionName),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  const getGroupByInfo = (groupKey: string) => {
    const group = permissionGroupsDictionary[groupKey] || {
      key: groupKey,
      name: "No Group Found",
    };
    return {
      name: group?.name,
      description: group?.description,
      key: group.key,
    };
  };

  const availablePermissionsListItems: ITransferListItem[] | undefined =
    availablePermissions?.map((permission) => ({
      key: permission.name,
      title: getPermissionNameType(permission.name),
      ...permission,
    })) ?? [];

  const selectedPermissionsListItems: ITransferListItem[] =
    selectedPermissions?.map((permission) => {
      return {
        key: permission.name,
        title: getPermissionNameType(permission.name),
        ...permission,
      };
    }) ?? [];

  const permissionGroupsDictionary: IPermissionGroupsDictionary =
    useMemo(() => {
      const dict: Record<
        string,
        {
          key: string;
          name: string;
          permissions: IPermission[];
          description?: string;
        }
      > = {};
      extraTabProps?.availablePermissions?.forEach((permission) => {
        const entityGroup = permission.entityKey || "No Category";
        if (!dict[entityGroup]) {
          dict[entityGroup] = {
            key: entityGroup,
            name: permission.entityName,
            description: permission.entityDescription,
            permissions: [],
          };
        }
        dict[entityGroup].permissions.push(permission);
      });
      return dict;
    }, [extraTabProps?.availablePermissions]);

  return (
    <ConfigAccordionHeader title={`${categoryName} Permissions`}>
      <TransferList
        availableTitle="Available Permissions"
        availableItems={availablePermissionsListItems}
        selectedTitle={`Selected Permissions (${selectedPermissionsListItems?.length})`}
        selectedItems={selectedPermissionsListItems}
        onAddItem={(permissionName) => addPermission(permissionName)}
        onRemoveItem={removePermission}
        groupBy="entityKey"
        getGroupByInfo={getGroupByInfo}
        disabled={isReadOnly}
      />
    </ConfigAccordionHeader>
  );
};

interface IPermissionGroupsDictionary {
  [key: string]: {
    key: string;
    name: string;
    permissions: IPermission[];
    description?: string;
  };
}
